/**
 * Compute the assignment timeline for a day.
 *
 * - Honours an optional `breakStart` slot - if provided, the break is inserted
 *   into the timeline at that time and assignments around it are split or
 *   shifted to maintain ordering.
 * - When `breakStart` is null/empty, we DEFAULT it to the midpoint of the
 *   working portion of the shift (see `autoBreakStart`). This positions the
 *   break sensibly without admins having to type it.
 *
 * @returns Array of timeline items in display order:
 *          { kind: "assignment" | "break", label, start, end, minutes, color?, note?, category_id? }
 */
export function buildTimeline({ start, end, breakMinutes = 0, breakStart = null, entries = [], categories = [] }) {
  if (!start || !end) return [];
  const startMin = toMin(start);
  const endMin = toMin(end);
  if (endMin <= startMin) return [];

  const items = (entries || [])
    // TP-AT.RS.7.3 -- order by start_time so the Day Timeline
    // strip visually matches the manager's intent regardless of
    // the order entries were authored. Entries without a
    // start_time sort to the end. The cursor walk below remains
    // unchanged (packs back-to-back inside the shift envelope).
    .slice()
    .sort((a, b) => {
      const as = a.start_time ? toMin(a.start_time) : Number.MAX_SAFE_INTEGER;
      const bs = b.start_time ? toMin(b.start_time) : Number.MAX_SAFE_INTEGER;
      return as - bs;
    })
    .filter(e => Number(e.minutes) > 0)
    .map(e => {
      const cat = categories.find(c => c.category_id === e.category_id);
      return {
        kind: "assignment",
        category_id: e.category_id,
        label: cat?.name || "Assignment",
        color: cat?.color || "#999",
        minutes: Number(e.minutes),
        note: e.note || null,
      };
    });

  const brk0 = Number(breakMinutes) || 0;
  // TP-AT.RS.7.2 -- when any entry uses a category with kind="away"
  // (e.g. the Break Away row in the new model), DO NOT also insert
  // a legacy break slot from the mirrored shift.break_minutes. The
  // Away entry IS the break, so inserting both produces a visual
  // duplicate in the Day Timeline strip.
  const hasAwayEntry = (entries || []).some((e) => {
    const c = (categories || []).find((x) => x.category_id === e.category_id);
    return c && c.kind === "away";
  });
  const brk = hasAwayEntry ? 0 : brk0;
  // If no explicit break_start_time was given, place it at the midpoint of
  // the working portion (i.e. excluding the break itself). This means a
  // 09:00–17:00 / 60-min break defaults to 13:00; 06:00–14:00 / 30-min to 10:00.
  const breakAt = brk > 0 ? (breakStart ? toMin(breakStart) : autoBreakStart(startMin, endMin, brk)) : null;

  // Walk through assignments sequentially. Insert break either positionally
  // (when breakStart is provided) or skip it (lump-sum mode).
  const out = [];
  let cursor = startMin;
  let breakInserted = breakAt == null;
  for (const item of items) {
    let remaining = item.minutes;
    while (remaining > 0) {
      // Should the break fire before/at the next slot?
      if (!breakInserted && cursor >= breakAt) {
        out.push({ kind: "break", label: "Break", minutes: brk,
                   start: fmt(cursor), end: fmt(cursor + brk) });
        cursor += brk; breakInserted = true; continue;
      }
      // Capacity in this segment before the next break or shift end
      const cap = breakInserted ? remaining : Math.min(remaining, breakAt - cursor);
      if (cap <= 0) {
        // Break fires now (cursor == breakAt)
        out.push({ kind: "break", label: "Break", minutes: brk,
                   start: fmt(cursor), end: fmt(cursor + brk) });
        cursor += brk; breakInserted = true; continue;
      }
      out.push({
        kind: "assignment",
        category_id: item.category_id,
        label: item.label,
        color: item.color,
        note: item.note,
        minutes: cap,
        start: fmt(cursor),
        end: fmt(cursor + cap),
      });
      cursor += cap;
      remaining -= cap;
    }
  }
  // Trailing break if we never hit it (e.g. break_at after all assignments end)
  if (!breakInserted && brk > 0) {
    out.push({ kind: "break", label: "Break", minutes: brk,
               start: fmt(cursor), end: fmt(cursor + brk) });
  }
  return out;
}

function toMin(hhmm) {
  const [h, m] = String(hhmm).split(":").map(Number);
  return h * 60 + (m || 0);
}
function fmt(mins) {
  const h = Math.floor(mins / 60), m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * Auto break-start time: midpoint of the FULL shift window (start → end).
 * Snapped to the nearest 5-min increment so the on-screen label is clean.
 *
 * Examples:
 *   09:00–17:00 / 60-min break  → 13:00   (midpoint of 09:00–17:00)
 *   08:30–17:00 / 60-min break  → 12:45
 *   06:00–14:00 / 30-min break  → 10:00
 *
 * Exposed so RosterPage can render an "(Auto)" tag in the editor while the
 * field is unset.
 */
export function autoBreakStart(startMin, endMin, breakMins) {
  // Defensive: zero-length shift falls back to startMin.
  if (endMin <= startMin) return startMin;
  // Centre the break on the shift midpoint so each side reads as "half a day".
  const raw = Math.floor((startMin + endMin) / 2);
  // Round to nearest 5 minutes for tidy display.
  return Math.round(raw / 5) * 5;
}

/** Convenience wrapper for callers that have HH:MM strings rather than minutes. */
export function autoBreakStartHHMM(start, end, breakMins) {
  if (!start || !end) return "";
  const s = toMin(start); const e = toMin(end);
  if (e <= s) return "";
  return fmt(autoBreakStart(s, e, Number(breakMins) || 0));
}
